import { Badge, Box, Text } from "@radix-ui/themes";
import { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

export const FileInput = (props: {
  value: File | null;
  onChange: (file: File | null) => void;
  multiple?: boolean;
  accept?: string;
  className?: string;
}) => {
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [size, setSize] = useState<null | { width: number; height: number }>({
    width: 0,
    height: 0,
  });
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  // Create and clean up object URL when props.value changes
  useEffect(() => {
    if (props.value) {
      const url = URL.createObjectURL(props.value);
      setObjectUrl(url);

      // Clean up previous URL
      return () => {
        URL.revokeObjectURL(url);
        setSize({ width: 0, height: 0 });
      };
    } else {
      setObjectUrl(null);
      setSize({ width: 0, height: 0 });
    }
  }, [props.value]);

  const handleFilesChange = async (files: FileList) => {
    setSize(null);
    props.onChange(files[0]);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
    handleFilesChange(event.dataTransfer.files);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleFilesChange(event.target.files!);
  };

  return (
    <Box
      className={twMerge(
        "w-full rounded-md border border-dashed cursor-pointer select-none p-2",
        isDragOver && "bg-yellow-500/20 hue-animation",
        props.className
      )}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onClick={() => {
        (fileRef.current || document.querySelector("input"))?.click();
      }}
    >
      {!!props.value && (
        <div className="flex flex-row space-x-2 items-center ">
          {!props.accept && objectUrl && (
            <img
              ref={imageRef}
              src={objectUrl}
              className="w-auto h-20 object-cover"
              style={{ maxWidth: "64px" }}
              alt={props.value.name}
              onLoad={(e: any) =>
                setSize({
                  width: e.target?.naturalWidth || 0,
                  height: e.target?.naturalHeight || 0,
                })
              }
            />
          )}
          <Text
            size="2"
            className="block text-left grow overflow-hidden"
            style={{ whiteSpace: "break-spaces", wordBreak: "break-all" }}
          >
            <span className="line-clamp-2">{props.value.name}</span>
            <div className="space-x-1">
              {!props.accept && size && (
                <Badge size="1">
                  {size.width}x{size.height}
                </Badge>
              )}
              <Badge size="1">
                {(props.value.size / 1024 / 1024).toFixed(2) + " MB"}
              </Badge>
            </div>
          </Text>
        </div>
      )}
      {!props.value && (
        <Text size="2" className="block text-center w-full">
          {!props.accept && t("general.files.images")}
          {!!props.accept && t("general.files.files")}
        </Text>
      )}
      <input
        ref={fileRef}
        type="file"
        multiple={props.multiple === false ? false : true}
        accept={props.accept || "image/jpg,image/jpeg,image/png,image/gif"}
        className="fixed w-full h-full opacity-10"
        style={{
          top: -1000,
          left: -1000,
          pointerEvents: "none",
        }}
        onChange={handleFileSelect}
      />
    </Box>
  );
};
